import React, { useState, useEffect, useRef } from 'react'
import { MessageKind } from 'uiComponents/messages'
import { BookingCodesService, UploadDetails } from './bookingCodesService'
import UploadPage from 'venue/bookingCodes/uploadPage'
import { LoginService } from 'http/loginService'

interface UploadVouchersPageProps {
    accountSlug: string
    bookingCodesService: BookingCodesService
    backofficeEndpoint: string
    loginService: LoginService
    replaceMessages: (id: string, status: MessageKind, text: string) => void
    removeAllMessages: () => void
}

export function UploadVouchersPage(props: UploadVouchersPageProps) {
    const busyRef = useRef(false)
    const accountRef = useRef(props.accountSlug)
    const [loading, setLoading] = useState<boolean>(false)
    const [uploadsHistoryList, setUploadsHistoryList] = useState<UploadDetails[]>([])

    async function getUploadHistoryList() {
        setLoading(true)
        busyRef.current = true
        try {
            const data = await props.bookingCodesService.getVouchersUploadHistoryList(props.accountSlug)
            setUploadsHistoryList(data)
        } catch {
            props.replaceMessages('unknown_error', 'error', 'Oops! Something went wrong. Please try again later.')
        } finally {
            setLoading(false)
            busyRef.current = false
        }
    }

    async function refreshHistoryList() {
        try {
            const data = await props.bookingCodesService.getVouchersUploadHistoryList(accountRef.current)
            setUploadsHistoryList(data)
        } catch {
            props.replaceMessages('unknown_error', 'error', 'Oops! Something went wrong. Please reload the page.')
        }
    }

    useEffect(() => {
        const interval = setInterval(() => {
            if (!busyRef.current) {
                refreshHistoryList()
            }
        }, 30000)
        return () => clearInterval(interval)
    }, [])

    useEffect(() => {
        accountRef.current = props.accountSlug
        getUploadHistoryList()
    }, [props.accountSlug])

    const onUpload = async (file: File) => {
        busyRef.current = true
        return await props.bookingCodesService.uploadVouchersCodes(file, props.accountSlug)
    }

    const reloadUploadsHistoryList = async () => {
        await getUploadHistoryList()
    }

    const getCsvDownloadHref = () => {
        const csvRows = [
            'code,valid_from,valid_to,percentage_reduction,flat_reduction,article_id,unlimited,amount,remainder_usable,validity_period_from,validity_period_to',
            '23423423421,2020-05-28,2020-12-31,20.0,,2048,0,1,0',
            '23423423422,2020-05-28,2020-12-31,,5.00,2049,2,1',
            'C3423423423,2020-05-28,2020-12-31,15.0,,2048|2049,0,22,1',
            '23423423422,2020-05-28,2020-12-31,,5.00,2049,2,1,,2020-12-25,2020-12-31',
            'C3423423423,2020-05-28,2020-12-31,15.0,,2048|2049,0,22,1,2020-12-25,2020-12-31',
        ]
        const csv = csvRows.join('\n')
        return 'data:text/csv;charset=utf-8,' + encodeURI(csv)
    }

    return (
        <UploadPage
            accountSlug={props.accountSlug}
            uploadsHistoryList={uploadsHistoryList}
            bookingCodesService={props.bookingCodesService}
            loginService={props.loginService}
            backofficeEndpoint={props.backofficeEndpoint}
            uploadType="vouchers"
            exampleCSVHref={getCsvDownloadHref()}
            exampleFileName="vouchers_upload_example"
            loading={loading}
            onUpload={onUpload}
            reloadUploadsHistoryList={reloadUploadsHistoryList}
            onUploadError={() => (busyRef.current = false)}
            replaceMessages={props.replaceMessages}
            removeAllMessages={props.removeAllMessages}
        />
    )
}
